import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { convertHumanReadableIntervalToMillis, convertMillisToHumanReadableInterval } from '@coreutils/dates/date.utils';

const intervalTypes = [
  {
    label: 'minute(s)',
    value: 'minutes',
  },
  {
    label: 'hour(s)',
    value: 'hours',
  },
  {
    label: 'day(s)',
    value: 'days',
  },
  {
    label: 'week(s)',
    value: 'weeks',
  },
  {
    label: 'month(s)',
    value: 'months',
  },
  {
    label: 'year(s)',
    value: 'years',
  },
];

const defaultIntervalValue = 24 * 60 * 60 * 1000; // equal to 1 day

interface DateIntervalFormValue {
  intervalValue: number;
  intervalType: string;
}

@Component({
    selector: 'kt-date-interval',
    templateUrl: './date-interval.component.html',
    styleUrls: ['./date-interval.component.scss'],
    standalone: false
})
export class DateIntervalComponent implements OnInit, OnDestroy {

  @Input() value!: number;
  @Output() valueChange: EventEmitter<number> = new EventEmitter();
  form!: UntypedFormGroup;
  readonly intervalTypes = intervalTypes;
  private readonly onDestroy$ = new Subject();
  constructor(
    private fb: UntypedFormBuilder,
  ) { }

  ngOnInit(): void {
    this.createForm(this.value);
    this.form.valueChanges.pipe(
      filter(({ intervalType, intervalValue }: DateIntervalFormValue) => {
        return Boolean(this.form.valid && intervalType && intervalValue);
      }),
      map(({ intervalValue, intervalType }: DateIntervalFormValue) => convertHumanReadableIntervalToMillis(
        intervalValue,
        intervalType as any)
      ),
      tap((value: number) => {
        this.valueChange.next(value);
      }),
      takeUntil(this.onDestroy$),
    ).subscribe();
  }

  private createForm(value: number) {
    const [intervalType, intervalValue] = convertMillisToHumanReadableInterval(value || defaultIntervalValue);
    this.form = this.fb.group({
      intervalValue: [
        intervalValue,
        Validators.compose([
          Validators.required,
          Validators.max(365),
        ])
      ],
      intervalType: [
        intervalType,
        Validators.compose([
          Validators.required,
        ])
      ],
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.complete();
  }

}
